import React, { useState, useEffect } from 'react';

function ModuleAssignments() {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const [pms, setPms] = useState([]);
  const [modules, setModules] = useState([]);
  const [progressRecords, setProgressRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    pm_id: '',
    module_id: '',
    due_date: '',
    total_steps: 5
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    await Promise.all([fetchPMs(), fetchModules(), fetchAssignments()]);
  };

  const fetchPMs = async () => {
    try {
      const response = await fetch(`${API_URL}/training/pms`, {
        headers: { 'x-user-email': 'manager@awnex.com' }
      });
      const data = await response.json();
      setPms(data.data || []);
    } catch (err) {
      console.error('Error fetching PMs:', err);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await fetch(`${API_URL}/training/modules`, {
        headers: { 'x-user-email': 'manager@awnex.com' }
      });
      const data = await response.json();
      setModules(data.data || []);
    } catch (err) {
      console.error('Error fetching modules:', err);
    }
  };

  const fetchAssignments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/training/progress`, {
        headers: { 'x-user-email': 'manager@awnex.com' }
      });
      const data = await response.json();
      setProgressRecords(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignModule = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/training/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': 'manager@awnex.com'
        },
        body: JSON.stringify({
          pmId: parseInt(assignmentForm.pm_id),
          moduleId: parseInt(assignmentForm.module_id),
          dueDate: assignmentForm.due_date,
          totalSteps: parseInt(assignmentForm.total_steps)
        })
      });
      
      if (response.ok) {
        alert('Module assigned successfully!');
        setShowAssignmentForm(false);
        setAssignmentForm({
          pm_id: '',
          module_id: '',
          due_date: '',
          total_steps: 5
        });
        fetchAssignments();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to assign module');
      }
    } catch (err) {
      setError(err.message);
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Set default due date to 30 days from now
  const getDefaultDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">Module Assignments</h2>
          <p className="text-sm text-gray-600 mt-1">Assign training modules to Project Managers</p>
        </div>
        <button
          onClick={() => {
            setAssignmentForm(prev => ({ ...prev, due_date: getDefaultDueDate() }));
            setShowAssignmentForm(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <span>+</span>
          Assign Module
        </button>
      </div>

      {/* Assignment Form Modal */}
      {showAssignmentForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Assign Module to PM</h3>
            <form onSubmit={handleAssignModule} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Project Manager *</label>
                <select
                  value={assignmentForm.pm_id}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, pm_id: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="">Select PM...</option>
                  {pms.map(pm => (
                    <option key={pm.id} value={pm.id}>
                      {pm.name} ({pm.email})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Training Module *</label>
                <select
                  value={assignmentForm.module_id}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, module_id: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="">Select Module...</option>
                  {modules.map(module => (
                    <option key={module.id} value={module.id}>
                      {module.module_code} - {module.module_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Due Date *</label>
                <input
                  type="date"
                  value={assignmentForm.due_date}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, due_date: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-gray-500 mt-1">Default: 30 days from today</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Total Steps</label>
                <input
                  type="number"
                  value={assignmentForm.total_steps}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, total_steps: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                  min="1"
                  max="20"
                />
                <p className="text-xs text-gray-500 mt-1">Number of completion checkpoints (typically 5)</p>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAssignmentForm(false);
                    setAssignmentForm({ pm_id: '', module_id: '', due_date: '', total_steps: 5 });
                  }}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Assigning...' : 'Assign Module'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading && progressRecords.length === 0 ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
          <p>Loading assignments...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PM Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Module
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quiz Score
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {progressRecords.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    <div className="py-4">
                      <p className="text-lg font-medium mb-2">No module assignments yet</p>
                      <p className="text-sm">Click "Assign Module" to assign training modules to your PMs</p>
                    </div>
                  </td>
                </tr>
              ) : (
                progressRecords.map((record) => (
                  <tr key={record.progress_id || record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{record.pm_name}</div>
                      <div className="text-sm text-gray-500">{record.pm_email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{record.module_code}</div>
                      <div className="text-sm text-gray-500 max-w-xs truncate">{record.module_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        record.status === 'Completed' ? 'bg-green-100 text-green-800' :
                        record.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                        record.status === 'Due Soon' ? 'bg-yellow-100 text-yellow-800' :
                        record.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {record.status || 'Not Started'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(record.assigned_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(record.due_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${record.percent_complete || 0}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">{Math.round(record.percent_complete || 0)}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {record.quiz_score !== null && record.quiz_score !== undefined ? (
                        <span className={record.quiz_score >= 80 ? 'text-green-600 font-semibold' : 'text-gray-600'}>
                          {Math.round(record.quiz_score)}%
                        </span>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ModuleAssignments;
